import React, { useEffect, useState } from 'react';
import { FaEye, FaCheckCircle, FaTimes, FaDownload, FaFilter } from 'react-icons/fa';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';

// ============ FIXED: Handle both with and without /api prefix ============
const getBaseURL = () => {
  const url = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const cleanUrl = url.replace(/\/$/, '');
  if (cleanUrl.endsWith('/api')) {
    return cleanUrl;
  }
  return `${cleanUrl}/api`;
};

const API_URL = getBaseURL();

const AdminInvestments = () => {
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchInvestments();
  }, []);

  const fetchInvestments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/investments/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvestments(response.data.investments);
    } catch (error) {
      console.error('Error fetching investments:', error);
      toast.error('Failed to load investments');
    } finally {
      setLoading(false);
    }
  };

  const verifyInvestment = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/investments/verify/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Investment verified successfully');
      fetchInvestments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Verification failed');
    }
  };

  const filteredInvestments = investments.filter(inv => {
    if (filter === 'all') return true;
    return inv.status === filter;
  });

  const getStatusBadge = (status) => {
    const styles = { pending: 'bg-yellow-100 text-yellow-800', active: 'bg-green-100 text-green-800', matured: 'bg-blue-100 text-blue-800', withdrawn: 'bg-gray-100 text-gray-800' };
    return <span className={`px-2 py-1 text-xs rounded-full ${styles[status] || 'bg-gray-100'}`}>{status}</span>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>Manage Investments - Admin</title></Helmet>
      <div className="bg-gray-50 min-h-screen py-8">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Manage Investments</h1>
              <p className="text-gray-600 mt-1">View and verify all investment requests</p>
            </div>
            <button className="flex items-center gap-2 border rounded-lg px-4 py-2 hover:bg-gray-50"><FaFilter /> Filter</button>
          </div>

          <div className="flex gap-2 mb-6 border-b">
            {['all', 'pending', 'active', 'matured', 'withdrawn'].map(tab => (
              <button key={tab} onClick={() => setFilter(tab)} className={`px-4 py-2 font-medium transition ${filter === tab ? 'text-green-600 border-b-2 border-green-600' : 'text-gray-500'}`}>
                {tab.charAt(0).toUpperCase() + tab.slice(1)} ({investments.filter(i => tab === 'all' ? true : i.status === tab).length})
              </button>
            ))}
          </div>

          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Investor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredInvestments.map((inv) => (
                    <tr key={inv._id}>
                      <td className="px-6 py-4 text-sm">#{inv._id.slice(-8)}</td>
                      <td className="px-6 py-4">
                        <div className="font-medium">{inv.user?.name}</div>
                        <div className="text-xs text-gray-500">{inv.user?.email}</div>
                      </td>
                      <td className="px-6 py-4 capitalize">{inv.plan}</td>
                      <td className="px-6 py-4 font-semibold">₹{inv.amount.toLocaleString()}</td>
                      <td className="px-6 py-4 text-sm">{new Date(inv.createdAt).toLocaleDateString()}</td>
                      <td className="px-6 py-4">{getStatusBadge(inv.status)}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button onClick={() => { setSelectedInvestment(inv); setShowModal(true); }} className="text-blue-600 hover:text-blue-700"><FaEye /></button>
                          {inv.status === 'pending' && <button onClick={() => verifyInvestment(inv._id)} className="text-green-600 hover:text-green-700"><FaCheckCircle /></button>}
                          <button className="text-gray-600 hover:text-gray-700"><FaDownload /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedInvestment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold">Investment Details</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-gray-500 text-sm">Investor Name</p><p className="font-semibold">{selectedInvestment.user?.name}</p></div>
                <div><p className="text-gray-500 text-sm">Email</p><p>{selectedInvestment.user?.email}</p></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-gray-500 text-sm">Plan</p><p className="capitalize font-semibold">{selectedInvestment.plan}</p></div>
                <div><p className="text-gray-500 text-sm">Amount</p><p className="font-bold text-green-600">₹{selectedInvestment.amount.toLocaleString()}</p></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-gray-500 text-sm">Interest Rate</p><p>{selectedInvestment.interestRate}% p.a.</p></div>
                <div><p className="text-gray-500 text-sm">Expected Returns</p><p>₹{selectedInvestment.expectedReturns?.toLocaleString()}</p></div>
              </div>
              <div><p className="text-gray-500 text-sm">Transaction UID</p><p className="font-mono text-sm">{selectedInvestment.paymentUID || 'N/A'}</p></div>
              {selectedInvestment.paymentScreenshot && <div><p className="text-gray-500 text-sm mb-2">Payment Screenshot</p><img src={selectedInvestment.paymentScreenshot} alt="Payment Proof" className="max-h-48 rounded-lg border" /></div>}
              <div className="pt-4 flex gap-3">
                {selectedInvestment.status === 'pending' && <button onClick={() => { verifyInvestment(selectedInvestment._id); setShowModal(false); }} className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700">Verify Investment</button>}
                <button onClick={() => setShowModal(false)} className="flex-1 border border-gray-300 py-2 rounded-lg hover:bg-gray-50">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminInvestments;